import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import BuildIcon from '@mui/icons-material/Build';
import ToolDialog from "./ToolDialog";

/**
 * ToolBox Component
 *
 * @param {Object[]} tools
 * @param {string}   tools[].toolname
 * @param {string}   tools[].toolversion
 * @param {string}   tools[].description
 * @param {React.ComponentType} tools[].tool_component  - Rendered inside ToolDialog as children
 * @param {Object}   map
 * @param {Object}   layers
 * @param {Function} onNewLayerCallback - Callback to add new layers to the map
 */
const ToolBox = ({project, tools = [], map, layers, mapContainer, onNewLayerCallback }) => {
  const [open, setOpen] = useState(false);
  const [activeTool, setActiveTool] = useState(null);

  const activeToolDef = activeTool !== null ? tools[activeTool] ?? null : null;
  const ActiveComponent = activeToolDef?.tool_component ?? null;

  const handleOpenTool = (i) => {
    setActiveTool(i);
    // setOpen(false);
  };

  const handleCloseTool = () => setActiveTool(null);

  return (
    <>
      <div style={{ position: "relative", display: "inline-block" }}>

          {/* ── Icon button — always visible, toggles panel ── */}
          <button
            onClick={() => setOpen(o => !o)}
            title="Tools"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 40, height: 40, padding: 0,
              border: `1px solid ${open ? "#1976d2" : "rgba(0,0,0,0.23)"}`,
              borderRadius: 4,
              background: open ? "rgba(25,118,210,0.08)" : "#fff",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "all 0.15s ease",
              color: open ? "#1976d2" : "#555",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = open ? "rgba(25,118,210,0.12)" : "rgba(0,0,0,0.04)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = open ? "rgba(25,118,210,0.08)" : "#fff"; }}
          >
            <BuildIcon />
          </button>

          {/* ── Tool list panel ── */}
          {open && (
            <div style={{
              position: "absolute", top: 0, right: "58px",
              width: 300, maxHeight: "85vh",
              background: "#fff", borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              display: "flex", flexDirection: "column",
              zIndex: 1300, overflow: "hidden",
            }}>
              <div style={{
                display: "flex", alignItems: "center",
                padding: "0 6px 0 16px",
                background: "#1976d2", minHeight: 44, flexShrink: 0,
              }}>
                <span style={{
                  fontFamily: "Roboto, Arial, sans-serif",
                  fontSize: 15, fontWeight: 500, color: "#fff", flex: 1,
                }}>
                  Tools
                </span>
                <button
                  onClick={() => setOpen(false)}
                  title="Close"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "6px", color: "#fff",
                    display: "flex", alignItems: "center", borderRadius: "50%",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div style={{ overflowY: "auto", flex: 1 }}>
                {tools.length === 0 ? (
                  <div style={{
                    padding: "28px 16px", textAlign: "center",
                    fontFamily: "Roboto, Arial, sans-serif",
                    fontSize: 13, color: "rgba(0,0,0,0.38)",
                  }}>
                    No tools installed
                  </div>
                ) : (
                  tools.map((tool, i) => (
                    <ToolCard
                      key={i}
                      tool={tool}
                      active={activeTool === i}
                      onOpen={() => handleOpenTool(i)}
                    />
                  ))
                )}
              </div>
            </div>
          )}
      </div>

      {/* ── Render dialog via portal into map container so position:absolute anchors correctly ── */}
      {ActiveComponent && mapContainer && createPortal(
        <ActiveComponent
          title={activeToolDef?.toolname ?? "Unnamed Tool"}
          map={map}
          layers={layers}
          onClose={handleCloseTool}
          onNewLayerCallback={onNewLayerCallback}
          project={project}
        />,
        mapContainer
      )}
    </>
  );
};

/* ── Tool Card ── */
const ToolCard = ({ tool, active, onOpen }) => (
  <div style={{
    padding: "14px 16px 12px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    background: active ? "rgba(25,118,210,0.04)" : "transparent",
  }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
      <span style={{
        fontFamily: "Roboto, Arial, sans-serif",
        fontSize: 14, fontWeight: 500, color: "rgba(0,0,0,0.87)",
      }}>
        {tool.toolname}
      </span>
      <span style={{
        fontFamily: "Roboto, Arial, sans-serif",
        fontSize: 12, color: "rgba(0,0,0,0.45)",
      }}>
        v{tool.toolversion}
      </span>
    </div>
    <p style={{
      margin: "0 0 10px",
      fontFamily: "Roboto, Arial, sans-serif",
      fontSize: 13, lineHeight: 1.5, color: "rgba(0,0,0,0.6)",
    }}>
      {tool.description}
    </p>
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <button
        onClick={onOpen}
        style={{
          fontFamily: "Roboto, Arial, sans-serif",
          fontSize: 12, fontWeight: 500,
          letterSpacing: "0.06em", textTransform: "uppercase",
          color: active ? "#fff" : "#1976d2",
          background: active ? "#1976d2" : "transparent",
          border: "1px solid rgba(25,118,210,0.5)",
          borderRadius: 4, padding: "4px 12px", cursor: "pointer",
          transition: "background 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "rgba(25,118,210,0.08)"; e.currentTarget.style.borderColor = "#1976d2"; } }}
        onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(25,118,210,0.5)"; } }}
      >
        {active ? "Active" : "Open"}
      </button>
    </div>
  </div>
);

export default ToolBox;
